import { useRequiredContext } from "@redotech/react-util/context";
import {
  ASSIGN_ACTION_UNASSIGN_USER_ID,
  ASSIGN_ACTION_UNASSIGN_USER_NAME,
} from "@redotech/redo-flows-common/rules/actions";
import type { AssignAction as AssignActionType } from "@redotech/redo-flows-common/rules/schemas";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo } from "react";
import { buildUserOptions } from "./build-user-options";

export const AssignAction = memo(function AssignAction({
  action,
  onChange,
  editableFields,
}: {
  action: AssignActionType;
  onChange: (updated: AssignActionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const userEditable =
    editableFields === undefined || editableFields.has("user");

  const team = useRequiredContext(TeamContext);

  const userOptions: RedoListItem<{ id: string; name: string }>[] =
    useMemo(() => {
      const userItems = buildUserOptions(team?.users).map((user) => ({
        id: user.id,
        type: "text" as const,
        text: user.name,
        value: user,
      }));

      const unassignItem: RedoListItem<{ id: string; name: string }> = {
        id: ASSIGN_ACTION_UNASSIGN_USER_ID,
        type: "text",
        text: ASSIGN_ACTION_UNASSIGN_USER_NAME,
        value: {
          id: ASSIGN_ACTION_UNASSIGN_USER_ID,
          name: ASSIGN_ACTION_UNASSIGN_USER_NAME,
        },
      };

      return [unassignItem, ...userItems];
    }, [team?.users]);

  const selectedUser = useMemo(() => {
    return userOptions.find((opt) => opt.value.id === action.user?.id);
  }, [userOptions, action.user?.id]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <RedoSingleSelectDropdownInput<{ id: string; name: string }>
        disabled={!userEditable}
        fitToAnchor
        label={t("User")}
        options={userOptions}
        optionSelected={({ item }) => {
          if (!userEditable) {
            return;
          }
          onChange({ ...action, user: item.value });
        }}
        selectedItem={selectedUser}
      />
    </Flex>
  );
});
